//#region Import
import { getPresignedUrl, getPresignedUrls } from "@/api/reportApi";
import {
  Apps,
  DicomBox,
  DicomInitialized,
  initDwvCommons,
  loadDicomUrls,
  loadDicomZip,
  updateAppLayout,
  updateOriginBox,
  focusDicomsTo3dPoint,
  updateSliceIntersection,
  setSliceIndex,
  get3dSlicePoints,
} from "../DicomManager";
import { getDrawBox2D, drawOn2DCanvas } from "@/components/dicom/slice.js";
import { jsPDF } from "jspdf";
import {
  showLoader,
  hideLoader,
  delay,
} from "@/components/controls/Loader.vue";
import {
  getFormattedDate,
  getFullDate,
  saveFile,
} from "@/components/reports/fileMethods";
import swal from "sweetalert";
import { drawBox, drawLine, drawPoint } from "@/components/draw/draw";
import { getTeethBox } from "@/components/occlusal/OcclusalTools.js";
import { showDicomCutReportDlg, fetchCoordMappings } from "./DicomCutReports";
import {
  isAnchorDataInitialized,
  generateRootCrownMeshes,
} from "@/components/anchorage/AnchorageCalculation.js";

//#endregion

//#region Fields

let dwv = require("dwv");

const tmjSize = 32;
let leftTmjPoint = null;
let rightTmjPoint = null;

let leftTmjInPoint = null;
let rightTmjInPoint = null;
let leftTmjOutPoint = null;
let rightTmjOutPoint = null;
let leftTmjShifts = null;
let rightTmjShifts = null;

let rightTmjAngle = null;
let leftTmjAngle = null;

let tmjRaycaster = new THREE.Raycaster(
  new THREE.Vector3(),
  new THREE.Vector3(),
); //   , 0.1,        100000

let _reportDicomInitialized = null;
let _reportApps = [new dwv.App(), new dwv.App(), new dwv.App()];
let _orientations = ["axial", "sagittal", "coronal"];
let _colors = ["red", "blue", "green"];
let _vFroms = [
  new THREE.Vector3(0, 1, 0),
  new THREE.Vector3(1, 0, 0),
  new THREE.Vector3(0, 0, 1),
];
let _dicomLoaded = false;
let _coordMappingMaxillaUrl = null;
let _coordMappingMandibleUrl = null;
let _panoramicMaxillaUrl = null;
let _panoramicMandibleUrl = null;
let _cutLinksInitialised = false;

let _cutDataInitialised = false;

//#endregion

async function generateCrossSectionReport() {
  console.log("generateCrossSectionReport");
  showLoader(`Preparing data`, `Please wait...`, true);
  await delay(100);

  if (!_reportDicomInitialized) {
    await loadReportDicomApps(generateCrossSectionReport);
    return;
  }

  findTmjPoints();
  for (const app of _reportApps) {
    const layerGroup = app.getLayerGroupByDivId(app.divId);
    const viewLayer = layerGroup.getActiveViewLayer();
    viewLayer.resetRotationMatrix();
    app.resetZoom();
  }

  const pdf = new jsPDF("landscape");
  const pdfWidth = pdf.internal.pageSize.getWidth(); // 210
  const pdfHeight = pdf.internal.pageSize.getHeight(); // 297

  const fontSize = 14;

  pdf.setFontSize(fontSize + 6);
  pdf.setFont("helvetica");
  const lineHeightFactor = 0.7;

  let text = "Images Reports";
  let textDims = pdf.getTextDimensions(text);
  let headerLeft = pdfWidth / 2 - textDims.w / 2;
  let headerTop = pdfHeight / 2 - textDims.h;
  pdf.text(text, headerLeft, headerTop);

  pdf.setFontSize(fontSize);
  let textTop = headerTop + textDims.h;

  let patientName = `${vueApp.patientFirstName} ${vueApp.patientLastName}`;
  textDims = pdf.getTextDimensions(patientName);
  let textHeight = textDims.h;
  let textWidth = textDims.w;
  let lineHeight = textHeight * 2 * lineHeightFactor;
  let textLeft = pdfWidth / 2 - textWidth / 2;
  pdf.text(patientName, textLeft, textTop);
  textTop += lineHeight;

  const imgLbX = 1;
  const imgLbY = 4;
  const imgX = 7;
  let imgFontSize = fontSize - 3;

  pdf.addPage();
  pdf.setFontSize(fontSize);
  pdf.text("Frontal Teeth Cross Sectional Images", 10, 14);

  pdf.setTextColor(255, 255, 255);
  pdf.setFontSize(imgFontSize);

  let imgGap = 5;
  let imgWidth = (pdfWidth - imgX * 2 - imgGap * 5) / 6;
  let imgHeight = imgWidth * 2;

  let imgLeft = imgX;
  let imgTop = 14 + lineHeight;
  let numbers = ["13", "12", "11", "21", "22", "23"];
  for (let i = 0; i < numbers.length; i++) {
    let imgData = getSaggitalImageData(numbers[i]);
    if (imgData)
      pdf.addImage(imgData, "JPEG", imgLeft, imgTop, imgWidth, imgHeight);
    pdf.text(numbers[i], imgLeft + imgLbX, imgTop + imgLbY);
    imgLeft += imgWidth + imgGap;
  }
  imgLeft = imgX;
  imgTop += imgHeight + 10;
  numbers = ["43", "42", "41", "31", "32", "33"];
  for (let i = 0; i < numbers.length; i++) {
    let imgData = getSaggitalImageData(numbers[i]);
    if (imgData)
      pdf.addImage(imgData, "JPEG", imgLeft, imgTop, imgWidth, imgHeight);
    pdf.text(numbers[i], imgLeft + imgLbX, imgTop + imgLbY);
    imgLeft += imgWidth + imgGap;
  }

  pdf.addPage();
  pdf.setTextColor(0, 0, 0);
  pdf.setFontSize(fontSize);
  pdf.text("Posterior Teeth Cross Sectional Images", 10, 20);
  pdf.setTextColor(255, 255, 255);
  pdf.setFontSize(imgFontSize);

  imgGap = 3;
  imgWidth = (pdfWidth - imgX * 2 - imgGap * 7) / 8;
  imgHeight = imgWidth * 2;

  imgLeft = imgX;
  imgTop = 20 + lineHeight * 2;
  numbers = ["17", "16", "15", "14", "24", "25", "26", "27"];
  for (let i = 0; i < numbers.length; i++) {
    let imgData = getSaggitalImageData(numbers[i]);
    if (imgData)
      pdf.addImage(imgData, "JPEG", imgLeft, imgTop, imgWidth, imgHeight);
    pdf.text(numbers[i], imgLeft + imgLbX, imgTop + imgLbY);
    imgLeft += imgWidth + imgGap;
  }
  imgLeft = imgX;
  imgTop += imgHeight + 10;
  numbers = ["47", "46", "45", "44", "34", "35", "36", "37"];
  for (let i = 0; i < numbers.length; i++) {
    let imgData = getSaggitalImageData(numbers[i]);
    if (imgData)
      pdf.addImage(imgData, "JPEG", imgLeft, imgTop, imgWidth, imgHeight);
    pdf.text(numbers[i], imgLeft + imgLbX, imgTop + imgLbY);
    imgLeft += imgWidth + imgGap;
  }

  pdf.addPage();
  pdf.setTextColor(0, 0, 0);
  pdf.setFontSize(fontSize);
  pdf.text("Coronal Images", 10, 14);
  pdf.setTextColor(255, 255, 255);
  pdf.setFontSize(imgFontSize);

  imgGap = 2;
  imgWidth = (pdfWidth - imgX * 2 - imgGap * 4) / 5;
  let coefW = 0.7;
  imgHeight = imgWidth * coefW;
  textDims = pdf.getTextDimensions("25");

  imgLeft = imgX;
  imgTop = 18 + lineHeight;
  numbers = [
    ["17", "27"],
    ["16", "26"],
    ["15", "25"],
    ["14", "24"],
    ["13", "23"],
  ];
  for (let i = 0; i < numbers.length; i++) {
    let arr = numbers[i];
    let imgData = getCoronalImageData(arr[0], arr[1], coefW);
    if (imgData)
      pdf.addImage(imgData, "JPEG", imgLeft, imgTop, imgWidth, imgHeight);
    pdf.text(arr[0], imgLeft + imgLbX, imgTop + imgLbY);
    pdf.text(arr[1], imgLeft + imgWidth - textDims.w - imgLbX, imgTop + imgLbY);

    imgLeft += imgWidth + imgGap;
  }

  imgLeft = imgX;
  imgTop += imgHeight + imgGap;
  numbers = [
    ["17", "27"],
    ["16", "26"],
    ["15", "25"],
    ["14", "24"],
    ["13", "23"],
  ];
  for (let i = 0; i < numbers.length; i++) {
    let arr = numbers[i];
    let imgData = getAxialImageData(arr[0], arr[1], coefW);
    if (imgData)
      pdf.addImage(imgData, "JPEG", imgLeft, imgTop, imgWidth, imgHeight);
    // pdf.text(arr[0], imgLeft + imgLbX, imgTop + imgLbY);
    // pdf.text(arr[1], imgLeft + imgWidth - textDims.w - imgLbX, imgTop + imgLbY);

    imgLeft += imgWidth + imgGap;
  }

  imgLeft = imgX;
  imgTop += imgHeight + imgGap;
  numbers = [
    ["47", "37"],
    ["46", "36"],
    ["45", "35"],
    ["44", "34"],
    ["43", "33"],
  ];
  for (let i = 0; i < numbers.length; i++) {
    let arr = numbers[i];
    let imgData = getCoronalImageData(arr[0], arr[1], coefW);
    if (imgData)
      pdf.addImage(imgData, "JPEG", imgLeft, imgTop, imgWidth, imgHeight);
    pdf.text(arr[0], imgLeft + imgLbX, imgTop + imgLbY);
    pdf.text(arr[1], imgLeft + imgWidth - textDims.w - imgLbX, imgTop + imgLbY);

    imgLeft += imgWidth + imgGap;
  }

  imgLeft = imgX;
  imgTop += imgHeight + imgGap;
  numbers = [
    ["47", "37"],
    ["46", "36"],
    ["45", "35"],
    ["44", "34"],
    ["43", "33"],
  ];
  for (let i = 0; i < numbers.length; i++) {
    let arr = numbers[i];
    let imgData = getAxialImageData(arr[0], arr[1], coefW);
    if (imgData)
      pdf.addImage(imgData, "JPEG", imgLeft, imgTop, imgWidth, imgHeight);
    // pdf.text(arr[0], imgLeft + imgLbX, imgTop + imgLbY);
    // pdf.text(arr[1], imgLeft + imgWidth - textDims.w - imgLbX, imgTop + imgLbY);

    imgLeft += imgWidth + imgGap;
  }

  pdf.addPage();
  pdf.setFontSize(fontSize);
  headerTop = 10;
  const leftColor = [0, 0, 255];
  const rightColor = [255, 165, 0];
  pdf.setTextColor(...leftColor);
  pdf.text("TMJ Left", 10, headerTop);
  pdf.setTextColor(...rightColor);
  pdf.text("TMJ Right", 4 + pdfWidth / 2, headerTop);
  pdf.setTextColor(255, 255, 255);
  if (leftTmjPoint && rightTmjPoint) {
    pdf.setFontSize(imgFontSize);

    imgGap = 2;
    imgWidth = (pdfWidth - imgX * 2 - imgGap * 5) / 6;
    imgHeight = imgWidth;
    imgLeft = imgX;
    imgTop = 8 + lineHeight;

    const borderWidth = 0.7;
    pdf.setLineWidth(borderWidth);

    let shifts = [-5, -2.5, 0, -5, -2.5, 0];
    // let shifts = [0, 0, 0, 0, 0, 0];
    for (let i = 0; i < shifts.length; i++) {
      let shift = shifts[i];
      let isLeft = i < 3;
      let imgData = getTmjImageData(isLeft, shift);
      if (isLeft) pdf.setDrawColor(...leftColor);
      else pdf.setDrawColor(...rightColor);
      pdf.rect(
        imgLeft - borderWidth / 2,
        imgTop - borderWidth / 2,
        imgWidth + borderWidth,
        imgHeight + borderWidth,
        "S",
      );

      if (imgData)
        pdf.addImage(imgData, "JPEG", imgLeft, imgTop, imgWidth, imgHeight);

      pdf.text(`${shift.toFixed(1)} mm`, imgLeft + imgLbX, imgTop + imgLbY);
      imgLeft += imgWidth + imgGap;
    }

    imgLeft = imgX;
    imgTop += imgHeight + imgGap;

    shifts = [2.5, 5, null, 2.5, 5, null];
    for (let i = 0; i < shifts.length; i++) {
      if (i == 3) {
        imgTop = 8 + lineHeight + imgHeight + imgGap;
        imgLeft = imgX + (imgHeight + imgGap) * 5;
      }
      let isLeft = i < 3;
      let shift = shifts[i];
      let imgData = shift
        ? getTmjImageData(isLeft, shift)
        : getTmjCoronalImageData(isLeft);

      if (isLeft) pdf.setDrawColor(...leftColor);
      else pdf.setDrawColor(...rightColor);
      pdf.rect(
        imgLeft - borderWidth / 2,
        imgTop - borderWidth / 2,
        imgWidth + borderWidth,
        imgHeight + borderWidth,
        "S",
      );
      if (imgData)
        pdf.addImage(imgData, "JPEG", imgLeft, imgTop, imgWidth, imgHeight);

      let text = shift ? `${shift.toFixed(1)} mm` : `Coronal`;
      pdf.text(text, imgLeft + imgLbX, imgTop + imgLbY);
      imgTop += imgWidth + imgGap;
    }

    imgLeft = imgX + imgWidth + imgGap; //(imgWidth + imgGap) * 1.5;
    imgTop = 8 + lineHeight + imgHeight + imgGap;
    imgWidth = imgWidth * 4 + imgGap * 3;
    imgHeight = imgHeight * 3 + imgGap * 2;
    let imgData = getTmjAxialData(imgHeight / imgWidth);
    if (imgData)
      pdf.addImage(imgData, "JPEG", imgLeft, imgTop, imgWidth, imgHeight);
  } else {
    pdf.setTextColor(255, 0, 0);
    pdf.text("ERROR: invalid mandible bone model", 10, headerTop * 2);
  }

  pdf.setTextColor(0, 0, 0);
  let panoramicUrl = await getPanoramicUrl();
  if (panoramicUrl) {
    const img = new Image();
    img.crossOrigin = ""; // Handle potential CORS issues
    img.src = panoramicUrl;

    img.onload = function () {
      pdf.addPage();
      pdf.setTextColor(0, 0, 0);
      pdf.setFontSize(fontSize);
      pdf.text("Panoramic", 10, 10);

      imgTop = 8 + lineHeight;

      const maxWidth = pdfWidth - imgX * 2;
      const maxHeight = pdfHeight - imgTop - imgX;
      const maxRel = maxHeight / maxWidth;
      const imgRel = img.height / img.width;
      if (maxRel >= imgRel) {
        imgWidth = maxWidth;
        imgHeight = imgWidth * imgRel;
      } else {
        imgHeight = maxHeight;
        imgWidth = imgHeight / imgRel;
      }

      imgLeft = (pdfWidth - imgWidth) / 2;
      pdf.addImage(img, "PNG", imgLeft, imgTop, imgWidth, imgHeight);

      saveCrossSectionPdf(pdf);
    };

    img.onerror = function () {
      saveCrossSectionPdf(pdf);
    };
  } else {
    saveCrossSectionPdf(pdf);
  }
}

async function generateCutsReport() {
  console.log("generateCutsReport");
  showLoader(`Preparing data`, `Please wait...`, true);
  // await delay(100);

  if (!_cutLinksInitialised) {
    _coordMappingMaxillaUrl = await getCoordMappingUrl(true);
    _coordMappingMandibleUrl = await getCoordMappingUrl(false);
    _panoramicMaxillaUrl = await getPanoramicDentationUrl(true);
    _panoramicMandibleUrl = await getPanoramicDentationUrl(false);

    _cutLinksInitialised = true;
  }

  if (!_coordMappingMaxillaUrl || !_panoramicMaxillaUrl) {
    hideLoader();
    swal({
      text: "No data for cuts report.",
    });
    return;
  }

  if (!_reportDicomInitialized) {
    await loadReportDicomApps(generateCutsReport);
    return;
  }
  if (!_cutDataInitialised) {
    await fetchCoordMappings(_coordMappingMaxillaUrl, _coordMappingMandibleUrl);
    generateRootCrownMeshes();
    _cutDataInitialised = true;
  }
  // hideLoader();
  showDicomCutReportDlg(_panoramicMaxillaUrl, _panoramicMandibleUrl);
}

//#region Dicom methods

async function loadReportDicomApps(afterDicomRendering) {
  if (!_reportDicomInitialized) {
    _reportDicomInitialized = true;

    // if (DicomInitialized) {
    //   _reportApps = Apps;
    //   afterDicomRendering();
    //   return;
    // }

    try {
      if (!vueApp.dicomLowLink) await loadDicomUrls();

      let dicomLink = vueApp.dicomHighLink
        ? vueApp.dicomHighLink
        : vueApp.dicomMiddleLink;
      if (!dicomLink) dicomLink = vueApp.dicomLowLink;
      // dicomLink = "demo/dicom.zip";

      let dicomZipData = await loadDicomZip(dicomLink, true);
      showLoader(`Preparing data`, `Please wait...`, true);
      console.log(
        "loadReportDicomApps",
        dicomZipData ? dicomZipData.byteLength : 0,
      );
      if (dicomZipData && dicomZipData.byteLength) {
        initDwvCommons();

        _reportApps.forEach((app, index) => {
          app.isMobileDevice = vueApp.isMobileDevice; //v-if="!isMobileDevice"
          app.index = index;
          app.isNoTeeth = true;
          app.isReadonly = true;
          app.viewIndex = app.index !== 0 ? app.index - 1 : 2;
          app.color = _colors[index];
          app.vFrom = _vFroms[index];
          app.orientation = _orientations[index];
          app.divId = `${app.orientation}reportDiv`;
          app.toolName = "dragDrop";
          app.opacity = 0.5;
          app.sliceOpacity = 0.25;
          app.outlineThickness = 1;
          app.annotationColor = "#CC5500";
          app.segmentationType = "fillAll";
          app.segmentations = ["report"];
          app.showSegmentation = true;
          app.showIntersection = true;
          app.show3DLayer = true;
          app.isLoading = false;
          app.isAnnotating = false;
          app.isEditingAnnotation = false;
          app.show3DFiller = true;
          app.annotationFillSegmentations = true;
          app.annotationOpacity = 0.5;
          app.text1 = app.text2 = app.text3 = "";
          app.visible = true;
          app.parentApp = 1;

          let appDiv = document.createElement("div");
          appDiv.id = app.divId;
          appDiv.style.width = appDiv.style.height = "1px";

          document.body.appendChild(appDiv);

          const viewConfig = new dwv.ViewConfig(app.divId);
          viewConfig.orientation = app.orientation;

          const viewConfigs = { "*": [viewConfig] };
          const options = new dwv.AppOptions(viewConfigs);
          options.viewOnFirstLoadItem = false;
          app.init(options);
          app.tools = options.tools;

          app.addEventListener("load", (event) => {
            console.log(`report app ${app.index} load`, event);
            // let stage = app.getStage();
            // const group = stage.getActiveLayerGroup();
            // const viewLayer = group.getActiveViewLayer();
            // console.log(`report app ${app.index} viewLayer`, viewLayer);
            app.render(event.loadid);
          });
          app.addEventListener("loadstart", (event) => {
            console.log(`report app ${app.index} loadstart`, event);
          });
          app.addEventListener("renderend", () => {
            if (!app.isRendered) {
              console.log(`report app ${app.index} renderend`, event);
              const layerGroup = app.getLayerGroupByDivId(app.divId);
              const activeLayer = layerGroup.getActiveViewLayer();

              app.baseSize = activeLayer.getBaseSize();
              const viewController = activeLayer.getViewController();
              app.worldSize = viewController.getImageWorldSize();
              app.spacing = viewController.get2DSpacing();
              app.geometry = viewController.getViewImage().getGeometry();
              // app.getImage(0).getGeometry();
              app.size = app.geometry.getSize().getValues();
              app.spacing = app.geometry.getSpacing().getValues();
              const origin = app.geometry.getOrigin();
              app.origin = [+origin.getX(), +origin.getY(), +origin.getZ()];

              let image = app.getLastImage();
              if (!image) image = viewController.getViewImage();
              if (image) {
                const size = image.getGeometry().getSize();
                app.max = size.get(app.viewIndex);
                app.current = Math.floor(app.max / 2) + 1;
              }
              updateOriginBox(app, viewController);
              app.isRendered = true;
              let appDiv = document.getElementById(app.divId);
              appDiv.style.display = "none";
              let renderedCount = _reportApps.filter(
                (x) => x.isRendered,
              ).length;
              if (renderedCount == _reportApps.length) {
                afterDicomRendering();
              }
            }
          });
          app.addEventListener("loadabort", (event) => {
            console.log(`report app ${app.index} loadabort`, event);
          });
          app.addEventListener("loaderror", (event) => {
            console.log(`report app ${app.index} loaderror`, event);
          });
        });

        let app0 = _reportApps[0];
        app0.addEventListener("loadend", (event) => {
          console.log(`report app 0 loadend`, event);
          const image0 = app0.getLastImage(); //getImage(0);
          if (!image0) {
            const layerGroup = app0.getLayerGroupByDivId(app0.divId);
            const activeLayer = layerGroup.getActiveViewLayer();
            const viewController = activeLayer.getViewController();
            image0 = viewController.getViewImage();
          }
          app0.isOrcaLoaded = true;
          for (let index = 1; index < _reportApps.length; index++) {
            const app = _reportApps[index];
            app.addEventListener("loadend", (event) => {
              console.log(`report app ${app.index} loadend`, event);
              app.isOrcaLoaded = true;
            });
            app.addNewImage(image0.clone());
          }
        });

        _reportApps[0].loadImageObject([
          { name: "dicom", filename: "dicom.zip", data: dicomZipData },
        ]);
      }
    } catch (e) {
      console.log(`loadReportDicomApps error: ${e}`);
    }
  }
}

function getDwvPos(vPosition, app) {
  const appGeometry = app.geometry;
  const dwvOrigin = appGeometry.getOrigin();
  // const dwvOrigin = new THREE.Vector3(
  //   origin.getX(),
  //   origin.getY(),
  //   origin.getZ()
  // );
  // vPosition.setX(_dicomBox.min.x + dwvPosition.x - dwvOrigin.x);
  // vPosition.setY(_dicomBox.min.y + dwvPosition.z - dwvOrigin.z);
  // vPosition.setZ(_dicomBox.max.z - dwvPosition.y - dwvOrigin.y);

  let x = vPosition.x - DicomBox.min.x + dwvOrigin.getX();
  let z = vPosition.y - DicomBox.min.y + dwvOrigin.getZ();
  let y = DicomBox.max.z - vPosition.z + dwvOrigin.getY();
  const dwvPosition = new dwv.Point([x, y, z, 0]);
  // new dwv.Point3D(x, y, z);
  return dwvPosition;
}

function convertDwvPosition(dwvPosition, dwvOrigin) {
  if (DicomBox) {
    // const appGeometry = app.geometry;
    // const dwvOrigin = appGeometry.getOrigin();
    const vPosition = new THREE.Vector3(
      DicomBox.min.x + dwvPosition.get(0) - dwvOrigin.getX(),
      DicomBox.min.y + dwvPosition.get(2) - dwvOrigin.getZ(),
      DicomBox.max.z - dwvPosition.get(1) + dwvOrigin.getY(),
    );
    return vPosition;
  }
}

function getImagesObjFromDwvPosition(
  app,
  dwvPosition,
  minPoint,
  maxPoint,
  lineArr,
  pointArr,
  figures,
  sharpValue,
) {
  if (!app || !dwvPosition) return;
  // console.log("getImagesObjFromPosition", dwvPosition);
  const viewLayer = app.getStage().getActiveLayerGroup().getActiveViewLayer();
  const viewController = viewLayer.getViewController();
  const view = viewController.getView();
  let imgData = viewLayer.getImageData();
  const imgWidth = imgData.width;
  const imgHeight = imgData.height;
  const imageZoom = 4;
  const tempWidth = imgWidth * imageZoom;
  const tempHeight = imgHeight * imageZoom;

  const dwvCanvas = document.createElement("canvas");
  dwvCanvas.width = imgWidth;
  dwvCanvas.height = imgWidth;
  const dwvContext = dwvCanvas.getContext("2d");

  if (dwvPosition) {
    const geometry = viewController.getViewImage().getGeometry();
    const index = geometry.worldToIndex(dwvPosition);
    // const ctx0 = dwvCanvas.getContext("2d");
    imgData = dwvContext.createImageData(imgWidth, imgHeight);
    view.generateImageData(imgData, index);
  }
  dwvContext.putImageData(imgData, 0, 0);

  if (figures && figures.length) {
    let drawingBox = getDrawBox2D(DicomBox, app);
    drawOn2DCanvas(figures, dwvContext, app, null, drawingBox);
    imgData = dwvContext.getImageData(0, 0, imgWidth, imgHeight);
  }

  const offscreenCanvas = document.createElement("canvas");
  offscreenCanvas.width = tempWidth;
  offscreenCanvas.height = tempHeight;
  const offscreenCtx = offscreenCanvas.getContext("2d");
  offscreenCtx.imageSmoothingEnabled = true;
  offscreenCtx.putImageData(imgData, 0, 0);
  offscreenCtx.drawImage(
    offscreenCanvas,
    0,
    0,
    imgWidth,
    imgHeight, // Source rectangle
    0,
    0,
    tempWidth,
    tempHeight, // Destination rectangle
  );

  const tempCanvas = document.createElement("canvas");
  tempCanvas.width = tempWidth;
  tempCanvas.height = tempHeight;
  const tempCtx = tempCanvas.getContext("2d");
  tempCtx.imageSmoothingEnabled = false;
  tempCtx.drawImage(offscreenCanvas, 0, 0);

  const canvas = document.createElement("canvas");
  canvas.width = tempWidth;
  canvas.height = tempHeight;
  const ctx = canvas.getContext("2d");
  let yFlip = app.index !== 0 ? -1 : 1;
  let xShift = 0;
  let yShift = app.index !== 0 ? canvas.height : 0;
  if (minPoint && maxPoint) {
    const minPos = viewController.convertWorldPosToPlanePos(minPoint);
    const maxPos = viewController.convertWorldPosToPlanePos(maxPoint);
    canvas.width = Math.abs(maxPos.x - minPos.x) * imageZoom;
    canvas.height = Math.abs(maxPos.y - minPos.y) * imageZoom;

    xShift = -minPos.x * imageZoom;
    yShift =
      app.index !== 0
        ? canvas.height + minPos.y * imageZoom
        : -minPos.y * imageZoom;
  }

  ctx.setTransform(1, 0, 0, yFlip, xShift, yShift);
  ctx.imageSmoothingEnabled = false;
  ctx.drawImage(tempCanvas, 0, 0);

  if (sharpValue && +sharpValue > 0)
    sharpen2(ctx, canvas.width, canvas.height, +sharpValue);

  const imagesObj = {
    baseImage: canvas.toDataURL("image/jpeg"),
    center0: dwvPosition.get(0),
    center: dwvPosition,
    min: minPoint,
    max: maxPoint,
    width: canvas.width,
    height: canvas.height,
    yFlip: yFlip,
    xShift: xShift,
    yShift: yShift,
    imageZoom: imageZoom,
    lines: [],
    popupLines: [],
    points: [],
  };

  // 400 = 1, 1600 = 4
  const lineWidth = canvas.height / 400;
  const pointRadius = lineWidth * 6;

  if (lineArr && lineArr.length) {
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.lineWidth = lineWidth;
    for (let i = 0; i < lineArr.length; i++) {
      const line = lineArr[i];
      const p1 = line.start; // arr[0];
      const p2 = line.end; // arr[1];
      const pos1 = viewController.convertWorldPosToPlanePos(p1);
      const pos2 = viewController.convertWorldPosToPlanePos(p2);

      let p1x = pos1.x * imageZoom + xShift;
      let p1y = pos1.y * imageZoom * yFlip + yShift;
      let p2x = pos2.x * imageZoom + xShift;
      let p2y = pos2.y * imageZoom * yFlip + yShift;

      ctx.fillStyle = ctx.strokeStyle = line.color; //arr[2];
      ctx.setLineDash(line.dashed ? [2 * imageZoom, 2 * imageZoom] : []);

      ctx.beginPath();
      ctx.moveTo(p1x, p1y);
      ctx.lineTo(p2x, p2y);
      ctx.stroke();

      let startPoint = {
        x: p1x / imagesObj.width,
        y: p1y / imagesObj.height,
        worldPos: p1,
      };
      let endPoint = {
        x: p2x / imagesObj.width,
        y: p2y / imagesObj.height,
        worldPos: p2,
      };
      imagesObj.lines.push({
        dwvLine: line,
        isNerve: line.isNerve,
        start: startPoint,
        end: endPoint,
      });
      imagesObj.popupLines.push({
        dwvLine: line,
        isNerve: line.isNerve,
        start: startPoint,
        end: endPoint,
      });
      // let startDwv = getDwvPosFromImagePoint(startPoint, imagesObj);
      // console.log("startDwv", p1, startDwv);
    }
  }

  if (pointArr && pointArr.length) {
    for (let i = 0; i < pointArr.length; i++) {
      const point = pointArr[i];
      const pos1 = viewController.convertWorldPosToPlanePos(point.pos);
      const p1x = pos1.x * imageZoom + xShift;
      const p1y = pos1.y * imageZoom * yFlip + yShift;

      ctx.fillStyle = ctx.strokeStyle = point.color;

      ctx.beginPath();
      ctx.arc(p1x, p1y, pointRadius, 0, Math.PI * 2);
      ctx.fill();
    }
  }

  // ctx.drawImage(tempCanvas, 0, 0);
  imagesObj.overlayImage = canvas.toDataURL("image/jpeg");
  if (app.inversePointMatrix)
    imagesObj.inversePointMatrix = app.inversePointMatrix.clone();
  if (app.pointMatrix) imagesObj.pointMatrix = app.pointMatrix.clone();
  if (app.geometry) imagesObj.dwvOrigin = app.geometry.getOrigin();
  // console.log("imagesObj", imagesObj);
  return imagesObj;
}

function sharpen(ctx, w, h, mix) {
  var x,
    sx,
    sy,
    r,
    g,
    b,
    a,
    dstOff,
    srcOff,
    wt,
    cx,
    cy,
    scy,
    scx,
    weights = [0, -1, 0, -1, 5, -1, 0, -1, 0],
    katet = Math.round(Math.sqrt(weights.length)),
    half = (katet * 0.5) | 0,
    dstData = ctx.createImageData(w, h),
    dstBuff = dstData.data,
    srcBuff = ctx.getImageData(0, 0, w, h).data,
    y = h;

  while (y--) {
    x = w;
    while (x--) {
      sy = y;
      sx = x;
      dstOff = (y * w + x) * 4;
      r = 0;
      g = 0;
      b = 0;
      a = 0;

      for (cy = 0; cy < katet; cy++) {
        for (cx = 0; cx < katet; cx++) {
          scy = sy + cy - half;
          scx = sx + cx - half;

          if (scy >= 0 && scy < h && scx >= 0 && scx < w) {
            srcOff = (scy * w + scx) * 4;
            wt = weights[cy * katet + cx];

            r += srcBuff[srcOff] * wt;
            g += srcBuff[srcOff + 1] * wt;
            b += srcBuff[srcOff + 2] * wt;
            a += srcBuff[srcOff + 3] * wt;
          }
        }
      }

      dstBuff[dstOff] = r * mix + srcBuff[dstOff] * (1 - mix);
      dstBuff[dstOff + 1] = g * mix + srcBuff[dstOff + 1] * (1 - mix);
      dstBuff[dstOff + 2] = b * mix + srcBuff[dstOff + 2] * (1 - mix);
      dstBuff[dstOff + 3] = srcBuff[dstOff + 3];
    }
  }

  ctx.putImageData(dstData, 0, 0);
}

function sharpen2(ctx, w, h, mix) {
  var x,
    sx,
    sy,
    r,
    g,
    b,
    a,
    dstOff,
    srcOff,
    wt,
    cx,
    cy,
    scy,
    scx,
    weights = [0, -1, 0, -1, 5, -1, 0, -1, 0],
    katet = Math.round(Math.sqrt(weights.length)),
    half = (katet * 0.5) | 0,
    dstData = ctx.createImageData(w, h),
    dstBuff = dstData.data,
    srcBuff = ctx.getImageData(0, 0, w, h).data,
    y = h;
  while (y--) {
    x = w;
    while (x--) {
      sy = y;
      sx = x;
      dstOff = (y * w + x) * 4;
      r = 0;
      g = 0;
      b = 0;
      a = 0;
      if (x > 0 && y > 0 && x < w - 1 && y < h - 1) {
        for (cy = 0; cy < katet; cy++) {
          for (cx = 0; cx < katet; cx++) {
            scy = sy + cy - half;
            scx = sx + cx - half;

            if (scy >= 0 && scy < h && scx >= 0 && scx < w) {
              srcOff = (scy * w + scx) * 4;
              wt = weights[cy * katet + cx];

              r += srcBuff[srcOff] * wt;
              g += srcBuff[srcOff + 1] * wt;
              b += srcBuff[srcOff + 2] * wt;
              a += srcBuff[srcOff + 3] * wt;
            }
          }
        }

        dstBuff[dstOff] = r * mix + srcBuff[dstOff] * (1 - mix);
        dstBuff[dstOff + 1] = g * mix + srcBuff[dstOff + 1] * (1 - mix);
        dstBuff[dstOff + 2] = b * mix + srcBuff[dstOff + 2] * (1 - mix);
        dstBuff[dstOff + 3] = srcBuff[dstOff + 3];
      } else {
        dstBuff[dstOff] = srcBuff[dstOff];
        dstBuff[dstOff + 1] = srcBuff[dstOff + 1];
        dstBuff[dstOff + 2] = srcBuff[dstOff + 2];
        dstBuff[dstOff + 3] = srcBuff[dstOff + 3];
      }
    }
  }

  ctx.putImageData(dstData, 0, 0);
}
//#endregion

//#region Teeth images

function getSaggitalImageData(toothNumber) {
  const app = _reportApps[1];
  const tooth = vueApp.getToothByNumber(toothNumber);
  const tmesh = tooth ? viewer.get_model_mesh(tooth.id) : null;
  if (tmesh && DicomBox) {
    let vPosition = tmesh.position;
    const dwvPosition = getDwvPos(vPosition, app);
    let toothBox = getMeshBox(tmesh);
    let sizeY = toothBox.size.y;
    let min = new THREE.Vector3(
      vPosition.x,
      vPosition.y - sizeY,
      vPosition.z + sizeY / 2,
    );
    let max = new THREE.Vector3(
      vPosition.x,
      vPosition.y + sizeY,
      vPosition.z - sizeY / 2,
    );
    // drawBox(toothBox);

    const minDwv = getDwvPos(min, app);
    const maxDwv = getDwvPos(max, app);
    let imgData = app.getImageFromPosition(dwvPosition, minDwv, maxDwv);
    return imgData;
  }
}

function getCoronalImageData(number1, number2, coefW) {
  const app = _reportApps[2];
  const tooth1 = vueApp.getToothByNumber(number1);
  const tooth2 = vueApp.getToothByNumber(number2);
  const tmesh1 = tooth1 ? viewer.get_model_mesh(tooth1.id) : null;
  const tmesh2 = tooth2 ? viewer.get_model_mesh(tooth2.id) : null;

  if (tmesh1 && tmesh2 && DicomBox) {
    let box1 = getMeshBox(tmesh1);
    let box2 = getMeshBox(tmesh2);
    let box = {
      min: {
        x: Math.min(box1.min.x, box2.min.x),
        y: Math.min(box1.min.y, box2.min.y),
        z: Math.min(box1.min.z, box2.min.z),
      },
      max: {
        x: Math.max(box1.max.x, box2.max.x),
        y: Math.max(box1.max.y, box2.max.y),
        z: Math.max(box1.max.z, box2.max.z),
      },
    };
    box = offsetBox(box);
    let vPosition = box.center;
    const dwvPosition = getDwvPos(vPosition, app);

    let sizeX = box.size.x;
    // let sizeY = box.size.y;
    let x2 = sizeX * 0.7;

    let min = new THREE.Vector3(
      vPosition.x - x2,
      vPosition.y - x2 * coefW,
      vPosition.z,
    );
    let max = new THREE.Vector3(
      vPosition.x + x2,
      vPosition.y + x2 * coefW,
      vPosition.z,
    );
    const minDwv = getDwvPos(min, app);
    const maxDwv = getDwvPos(max, app);
    let imgData = app.getImageFromPosition(dwvPosition, minDwv, maxDwv);
    return imgData;
  }
}

function getAxialImageData(number1, number2, coefW, isMaxilla) {
  const app = _reportApps[0];
  const tooth1 = vueApp.getToothByNumber(number1);
  const tooth2 = vueApp.getToothByNumber(number2);
  const tmesh1 = tooth1 ? viewer.get_model_mesh(tooth1.id) : null;
  const tmesh2 = tooth2 ? viewer.get_model_mesh(tooth2.id) : null;

  if (tmesh1 && tmesh2 && DicomBox) {
    let teetBox = offsetBox(getTeethBox(isMaxilla));

    let box1 = getMeshBox(tmesh1);
    let box2 = getMeshBox(tmesh2);
    let box = {
      min: {
        x: Math.min(box1.min.x, box2.min.x),
        y: Math.min(box1.min.y, box2.min.y),
        z: Math.min(box1.min.z, box2.min.z),
      },
      max: {
        x: Math.max(box1.max.x, box2.max.x),
        y: Math.max(box1.max.y, box2.max.y),
        z: Math.max(box1.max.z, box2.max.z),
      },
    };
    box = offsetBox(box);
    let vPosition = new THREE.Vector3(
      teetBox.center.x,
      box.center.y,
      teetBox.center.z,
    );
    const dwvPosition = getDwvPos(vPosition, app);
    const lineYpoint = getDwvPos(box.center, app);

    let sizeX = teetBox.size.x;
    // let sizeY = box.size.y;
    let x2 = sizeX * 0.6;

    let min = new THREE.Vector3(
      vPosition.x - x2,
      vPosition.y,
      vPosition.z + x2 * coefW,
    );
    let max = new THREE.Vector3(
      vPosition.x + x2,
      vPosition.y,
      vPosition.z - x2 * coefW,
    );
    const minDwv = getDwvPos(min, app);
    const maxDwv = getDwvPos(max, app);

    let lineArr = {
      start: new dwv.Point([minDwv.get(0), lineYpoint.get(1), minDwv.get(2)]),
      end: new dwv.Point([maxDwv.get(0), lineYpoint.get(1), maxDwv.get(2)]),
      color: "white",
      dashed: false,
    };
    let imgData = app.getImageFromPosition(dwvPosition, minDwv, maxDwv, [
      lineArr,
    ]);
    return imgData;
  }
}

//#endregion

//#region TMJ images

function findTmjPoints() {
  const mandible = vueApp.bones.find((x) => !x.isMaxilla);
  const mdmh = mandible ? viewer.get_model_mesh(mandible.id) : null;
  const zThreshold = 20;
  if (mdmh && !leftTmjPoint) {
    let mbox = getMeshBox(mdmh);
    leftTmjPoint = new THREE.Vector3(mbox.min.x, mbox.max.y, mbox.min.z);
    rightTmjPoint = new THREE.Vector3(mbox.max.x, mbox.max.y, mbox.min.z);

    let middleX = mbox.center.x;
    let maxLeftY = -Infinity;
    let maxRightY = -Infinity;

    let geometry = mdmh.defaultGeometry ? mdmh.defaultGeometry : mdmh.geometry;
    let position = mdmh.defaultPosition ? mdmh.defaultPosition : mdmh.position;

    const pos = geometry.getAttribute("position");
    const maxZ = mbox.min.z + zThreshold;
    for (let i = 0; i < pos.count; i++) {
      const x = pos.getX(i);
      const y = pos.getY(i);
      const z = pos.getZ(i);
      if (x < middleX) {
        if (y > maxLeftY && z < maxZ) {
          maxLeftY = y;
          leftTmjPoint.set(x, y, z);
        }
      } else {
        if (y > maxRightY && z < maxZ) {
          maxRightY = y;
          rightTmjPoint.set(x, y, z);
        }
      }
    }

    leftTmjPoint.add(position);
    rightTmjPoint.add(position);

    // tmjRaycaster.params.Points.threshold = 600;
    let tempSide = mdmh.material.side;
    mdmh.material.side = THREE.DoubleSide;
    let leftBox = getBoxFromPoint(leftTmjPoint, tmjSize / 3);
    let rightBox = getBoxFromPoint(rightTmjPoint, tmjSize / 3);
    // drawBox(rightBox);
    let maxLeftDistance = 0;
    let maxRightDistance = 0;
    let tempAngle = 0;
    let tempPoint = null;

    const minAngle = 14;
    const maxAngle = 25;

    for (let i = 0; i < pos.count; i++) {
      let meshPoint = new THREE.Vector3(
        pos.getX(i),
        pos.getY(i),
        pos.getZ(i),
      ).add(position);
      const isLeft = leftBox.containsPoint(meshPoint);
      const isRight = rightBox.containsPoint(meshPoint);
      if (isLeft || isRight) {
        let box = isLeft ? leftBox : rightBox;
        let tmjPoint = isLeft ? leftTmjPoint : rightTmjPoint;
        let opoint = getOppositePoint(mdmh, tmjPoint, meshPoint);
        if (opoint && box.containsPoint(opoint)) {
          let distance = getDistance(opoint, meshPoint);
          if (isLeft && distance > maxLeftDistance) {
            let inPoint = opoint.x > meshPoint.x ? opoint : meshPoint;
            let outPoint = opoint.x > meshPoint.x ? meshPoint : opoint;
            tempAngle = -getXAngle(outPoint, inPoint);
            let absAngle = Math.abs(tempAngle);
            if (tempAngle > 0 && absAngle >= minAngle && absAngle <= maxAngle) {
              maxLeftDistance = distance;
              leftTmjInPoint = inPoint;
              leftTmjOutPoint = outPoint;
            }
          } else if (isRight && distance > maxRightDistance) {
            let inPoint = opoint.x > meshPoint.x ? meshPoint : opoint;
            let outPoint = opoint.x > meshPoint.x ? opoint : meshPoint;
            tempAngle = -getXAngle(inPoint, outPoint);
            let absAngle = Math.abs(tempAngle);
            if (tempAngle < 0 && absAngle >= minAngle && absAngle <= maxAngle) {
              maxRightDistance = distance;
              rightTmjInPoint = inPoint;
              rightTmjOutPoint = outPoint;
            }
          }
        }
      }
    }
    mdmh.material.side = tempSide;
    leftTmjPoint = getMidPoint(leftTmjInPoint, leftTmjOutPoint);
    rightTmjPoint = getMidPoint(rightTmjInPoint, rightTmjOutPoint);

    if (!leftTmjPoint || !rightTmjPoint) return;

    if (leftTmjOutPoint.x > leftTmjInPoint.x) {
      tempPoint = leftTmjOutPoint;
      leftTmjOutPoint = leftTmjInPoint;
      leftTmjInPoint = tempPoint;
    }
    if (rightTmjOutPoint.x < rightTmjInPoint.x) {
      tempPoint = rightTmjOutPoint;
      rightTmjOutPoint = rightTmjInPoint;
      rightTmjInPoint = tempPoint;
    }
    // drawLine(leftTmjInPoint, leftTmjOutPoint, 0x0000ff);
    // drawPoint(leftTmjOutPoint, 0xff0000);
    // drawPoint(leftTmjInPoint, 0x00ff00);

    leftTmjAngle = -getXAngle(leftTmjOutPoint, leftTmjInPoint);
    console.log("leftTmjAngle in degrees:", leftTmjAngle);
    rightTmjAngle = -getXAngle(rightTmjInPoint, rightTmjOutPoint);
    console.log("rightTmjAngle in degrees:", rightTmjAngle);
    let perpLeft = getPerpendicularVector3D(leftTmjInPoint, leftTmjOutPoint);
    let perpRight = getPerpendicularVector3D(rightTmjInPoint, rightTmjOutPoint);

    leftTmjShifts = [
      { offset: -5, point: null },
      { offset: -2.5, point: null },
      { offset: 0, point: null },
      { offset: 2.5, point: null },
      { offset: 5, point: null },
    ];
    rightTmjShifts = [
      { offset: -5, point: null },
      { offset: -2.5, point: null },
      { offset: 0, point: null },
      { offset: 2.5, point: null },
      { offset: 5, point: null },
    ];

    for (const shift of leftTmjShifts) {
      let color = shift.offset ? 0xffff00 : 0xff0000;
      let scalar = shift.offset ? -20 : -50;
      shift.point = getShiftPoint(leftTmjPoint, leftTmjInPoint, shift.offset);
      // drawPoint(shift.point, color);
      let opposite = perpLeft.clone().multiplyScalar(scalar).add(shift.point);
      // drawLine(opposite, shift.point, color);
      shift.opposite = opposite;
    }
    for (const shift of rightTmjShifts) {
      let color = shift.offset ? 0xffff00 : 0xff0000;
      let scalar = shift.offset ? 20 : 50;
      shift.point = getShiftPoint(rightTmjPoint, rightTmjInPoint, shift.offset);
      // drawPoint(shift.point, color);
      let opposite = perpRight.clone().multiplyScalar(scalar).add(shift.point);
      // drawLine(opposite, shift.point, color);
      shift.opposite = opposite;
    }
    // drawPoint(leftTmjOutPoint, 0xffff00);
    // drawPoint(rightTmjOutPoint, 0xff00ff);
  }
}

function getTmjImageData(isLeft, offset) {
  const app = _reportApps[1];
  const app0 = _reportApps[0];

  const mandible = vueApp.bones.find((x) => !x.isMaxilla);
  const mesh = mandible ? viewer.get_model_mesh(mandible.id) : null;
  let shifts = isLeft ? leftTmjShifts : rightTmjShifts;
  const shift = shifts.find((s) => s.offset == offset);
  if (mesh && DicomBox && shift) {
    let vPosition = shift.point;
    // let vPosition = isLeft ? leftTmjPoint.clone() : rightTmjPoint.clone();
    // vPosition.x += isLeft ? offset : -offset;
    let min = new THREE.Vector3(
      vPosition.x,
      vPosition.y - tmjSize,
      vPosition.z + tmjSize,
    );
    let max = new THREE.Vector3(
      vPosition.x,
      vPosition.y + tmjSize,
      vPosition.z - tmjSize,
    );
    const dwvPosition = getDwvPos(vPosition, app);
    const minDwv = getDwvPos(min, app);
    const maxDwv = getDwvPos(max, app);

    let tmjAngle = isLeft ? leftTmjAngle : rightTmjAngle;
    if (tmjAngle) {
      let indexPos = app0.geometry.worldToIndex(dwvPosition);
      let size = app0.geometry.getSize();
      let originX = indexPos.get(0) / size.get(0);
      let originY = indexPos.get(1) / size.get(1);
      let originZ = indexPos.get(2) / size.get(2);
      let origin = [originX, originY, originZ];
      console.log("getTmjImageData origin", origin);
      const layerGroup = app.getLayerGroupByDivId(app.divId);
      const viewLayer = layerGroup.getActiveViewLayer();
      viewLayer.setRotationAngles(0, 0, tmjAngle, app.index, origin);
    }

    let imgData = app.getImageFromPosition(
      dwvPosition,
      minDwv,
      maxDwv,
      null,
      isLeft,
    );
    return imgData;
  }
}

function getTmjCoronalImageData(isLeft) {
  const app = _reportApps[2];
  const mandible = vueApp.bones.find((x) => !x.isMaxilla);
  const mesh = mandible ? viewer.get_model_mesh(mandible.id) : null;
  if (mesh && DicomBox) {
    let vPosition = isLeft ? leftTmjPoint.clone() : rightTmjPoint.clone();
    let min = new THREE.Vector3(
      vPosition.x - tmjSize,
      vPosition.y - tmjSize,
      vPosition.z,
    );
    let max = new THREE.Vector3(
      vPosition.x + tmjSize,
      vPosition.y + tmjSize,
      vPosition.z,
    );
    const dwvPosition = getDwvPos(vPosition, app);
    const minDwv = getDwvPos(min, app);
    const maxDwv = getDwvPos(max, app);

    let lineArr = [];
    let shifts = isLeft ? leftTmjShifts : rightTmjShifts;
    for (const shift of shifts) {
      const dwvPos = getDwvPos(shift.point, app);
      let isMain = !shift.offset;
      lineArr.push({
        start: new dwv.Point([dwvPos.get(0), minDwv.get(1), minDwv.get(2), 0]),
        end: new dwv.Point([dwvPos.get(0), maxDwv.get(1), maxDwv.get(2), 0]),
        color: isMain ? "red" : "yellow",
        dashed: !isMain,
      });
    }

    let tmjAngle = isLeft ? leftTmjAngle : rightTmjAngle;
    if (tmjAngle) {
      let indexPos = app.geometry.worldToIndex(dwvPosition);
      let size = app.geometry.getSize();
      let originX = indexPos.get(0) / size.get(0);
      let originY = indexPos.get(1) / size.get(1);
      let originZ = indexPos.get(2) / size.get(2);
      let origin = [originX, originY, originZ];
      const layerGroup = app.getLayerGroupByDivId(app.divId);
      const viewLayer = layerGroup.getActiveViewLayer();
      viewLayer.setRotationAngles(0, 0, tmjAngle, app.index, origin);
    }

    let imgData = app.getImageFromPosition(
      dwvPosition,
      minDwv,
      maxDwv,
      lineArr,
    );
    return imgData;
  }
}

function getTmjAxialData(coefW) {
  const app = _reportApps[0];
  let midTmj = getMidPoint(leftTmjPoint, rightTmjPoint);
  let fullBox = offsetBox(DicomBox);
  let vPosition = new THREE.Vector3(
    fullBox.center.x,
    midTmj.y,
    fullBox.center.z,
  );
  const dwvPosition = getDwvPos(vPosition, app);
  let sizeX = Math.abs(rightTmjPoint.x - leftTmjPoint.x);
  let x2 = sizeX * 0.75;
  let min = new THREE.Vector3(
    vPosition.x - x2,
    vPosition.y,
    vPosition.z + x2 * coefW,
  );
  let max = new THREE.Vector3(
    vPosition.x + x2,
    vPosition.y,
    vPosition.z - x2 * coefW,
  );
  const minDwv = getDwvPos(min, app);
  const maxDwv = getDwvPos(max, app);

  let lineArr = [];
  for (const shift of leftTmjShifts.concat(rightTmjShifts)) {
    let isLeft = leftTmjShifts.includes(shift);
    const dwvPos = getDwvPos(shift.point, app);
    const opPos = getDwvPos(shift.opposite, app);
    let isMain = !shift.offset;
    lineArr.push({
      start: new dwv.Point([dwvPos.get(0), dwvPos.get(1), dwvPos.get(2), 0]),
      end: new dwv.Point([opPos.get(0), opPos.get(1), opPos.get(2), 0]),
      color: isMain ? "red" : "yellow",
      dashed: !isMain,
    });
  }

  let imgData = app.getImageFromPosition(dwvPosition, minDwv, maxDwv, lineArr);
  return imgData;
}

//#endregion

//#region Help methods

function saveCrossSectionPdf(pdf) {
  let fullName = `crossSectionReport_${getFormattedDate(true)}.pdf`;
  pdf.save(fullName);
  hideLoader();
}

async function getCoordMappingUrl(isMaxilla) {
  // https://d3lk8scmwriwsa.cloudfront.net/STL/dicom-3223161773184575931/images/coord_mapping.json
  let dentation = isMaxilla ? "maxilla" : "mandible";
  try {
    let coordMappingUrl = await checkUrl(
      `${vueApp.stlBaseUrl}/images/${dentation}/coord_mapping.json`,
    );
    if (!coordMappingUrl || !coordMappingUrl.length)
      coordMappingUrl = await checkUrl(
        `${vueApp.stlBaseUrl}/images/coord_mapping.json`,
      );
    return coordMappingUrl;
  } catch (e) {
    console.log(`getCoordMappingUrl error: ${e}`);
  }
}

async function getPanoramicDentationUrl(isMaxilla) {
  let hasNerves =
    vueApp.nerves.length &&
    !!viewer.get_model_mesh(vueApp.nerves[0].id).geometry;
  let panoFile = hasNerves ? "pano_nerve.png" : "pano_mean.png";
  let dentation = isMaxilla ? "maxilla" : "mandible";

  let panoramicUrl = await checkUrl(
    `${vueApp.stlBaseUrl}/images/${dentation}/${panoFile}`,
  );

  if (!panoramicUrl || !panoramicUrl.length)
    panoramicUrl = await checkUrl(`${vueApp.stlBaseUrl}/images/${panoFile}`);

  if (hasNerves) {
    if (!panoramicUrl || !panoramicUrl.length)
      panoramicUrl = await checkUrl(
        `${vueApp.stlBaseUrl}/images/pano_mean.png`,
      );
  }

  if (panoramicUrl && panoramicUrl.length) {
    panoramicUrl = await toDataURL(panoramicUrl);
    return panoramicUrl;
  }
}

async function toDataURL(src) {
  const response = await fetch(src);
  const blob = await response.blob();
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.readAsDataURL(blob);
  });
}

async function checkUrl(url) {
  try {
    let response = await fetch(url, {
      method: "HEAD",
      mode: "cors",
      cache: "no-store",
    });
    if (response.ok) {
      return url;
    } else {
      let presignedUrl = await getPresignedUrl(url);
      if (presignedUrl && presignedUrl.length) return presignedUrl;
    }
  } catch (e) {
    console.log(`getPanoramicUrl error: ${e}`);
  }
}

async function getPanoramicUrl(isNerve) {
  // https://d3lk8scmwriwsa.cloudfront.net/STL/dicom-3190231742840863881/images/pano_mean.png
  let panoramicUrl = `${vueApp.stlBaseUrl}/images/pano_mean.png`;
  let panoramicNerveUrl = `${vueApp.stlBaseUrl}/images/pano_nerve.png`;
  // let panoramicNerveUrl = `${vueApp.stlBaseUrl}/images/maxilla/pano_nerve.png`;

  if (isNerve) {
    try {
      let response = await fetch(panoramicNerveUrl, {
        method: "HEAD",
        mode: "cors",
        cache: "no-cache",
      });
      if (response.ok) {
        return panoramicNerveUrl;
      }
    } catch (e) {
      console.log(`panoramicNerveUrl error: ${e}`);
    }
  }

  try {
    let response = await fetch(panoramicUrl, {
      method: "HEAD",
      mode: "cors",
      cache: "no-store",
    });
    if (response.ok) {
      return panoramicUrl;
    }
  } catch (e) {
    console.log(`getPanoramicUrl error: ${e}`);
  }
}

function getOppositePoint(mesh, dirPoint, meshPoint) {
  let retval = null;

  let direction = new THREE.Vector3(dirPoint.x, meshPoint.y, dirPoint.z);
  // drawLine(meshPoint, direction);
  direction.subVectors(direction, meshPoint);
  direction.normalize();

  tmjRaycaster.set(meshPoint, direction);
  let intersects = tmjRaycaster.intersectObject(mesh);
  let maxDistance = 0;
  for (const intersect of intersects) {
    let distance = intersect.distance; // getDistance(intersect.point, meshPoint); // intersect.distance; //
    if (distance > maxDistance) {
      retval = intersect.point;
      maxDistance = distance;
    }
  }

  return retval;
}

function getXAngle(point1, point2) {
  // 1. Define two points
  // const point1 = new THREE.Vector3(0, 0, 0); // Start point (e.g., the origin)
  // const point2 = new THREE.Vector3(5, 5, 0); // End point

  // If your points are not from the origin, calculate the difference vector
  const directionVector = new THREE.Vector3();
  directionVector.subVectors(point2, point1); // vector from point1 to point2

  // 2. Extract components for atan2 (using Y and X for the XY plane)
  const xDist = directionVector.x;
  const yDist = directionVector.z;

  // 3. Calculate the angle using Math.atan2(y, x)
  // The result is in radians, ranging from -PI to PI
  const angleRadians = Math.atan2(yDist, xDist);

  // Optional: Convert radians to degrees
  const angleDegrees = angleRadians * (180 / Math.PI);
  return angleDegrees;
}

function getPerpendicularVector3D(pointA, pointB) {
  // 1. Get the direction vector from A to B
  const direction = pointB.clone().sub(pointA).normalize();

  // 2. Define a reference vector (e.g., the world's Y-axis/up direction)
  const referenceVector = new THREE.Vector3(0, 1, 0);

  // 3. Calculate the cross product to get a perpendicular vector
  const perpendicular = new THREE.Vector3().crossVectors(
    direction,
    referenceVector,
  );

  // Check if the result is a zero vector (happens if direction and reference are parallel)
  if (perpendicular.lengthSq() === 0) {
    // If they are parallel (e.g., the line is pointing straight up or down),
    // use a different reference vector (e.g., the world's X-axis)
    const alternateReference = new THREE.Vector3(1, 0, 0);
    perpendicular.crossVectors(direction, alternateReference).normalize();
  } else {
    perpendicular.normalize();
  }

  return perpendicular;
}

function getMidPoint(p1, p2) {
  return p1 && p2
    ? new THREE.Vector3((p1.x + p2.x) / 2, (p1.y + p2.y) / 2, (p1.z + p2.z) / 2)
    : null;
}

function getShiftPoint(sourcePoint, dirPoint, offset) {
  if (!offset) return sourcePoint;
  const direction = new THREE.Vector3()
    .subVectors(dirPoint, sourcePoint)
    .normalize();
  const shiftPoint = new THREE.Vector3()
    .copy(sourcePoint)
    .addScaledVector(direction, offset);
  return shiftPoint;
}

function getDistance(point1, point2) {
  var dx = point1.x - point2.x;
  var dy = point1.y - point2.y;
  var dz = point1.z - point2.z;
  return Math.sqrt(dx * dx + dy * dy + dz * dz);
}

function getBoxFromPoint(center, shift) {
  // let box = {
  //   min: {
  //     x: center.x - shift,
  //     y: center.y - shift,
  //     z: center.z - shift,
  //   },
  //   max: {
  //     x: center.x + shift,
  //     y: center.y + shift,
  //     z: center.z + shift,
  //   },
  // };
  // box = offsetBox(box);
  let box = new THREE.Box3(
    new THREE.Vector3(center.x - shift, center.y - shift, center.z - shift),
    new THREE.Vector3(center.x + shift, center.y + shift, center.z + shift),
  );
  return box;
}

function getMeshBox(mesh) {
  if (mesh) {
    const position = mesh.position;
    const geometry = mesh.geometry;
    geometry.computeBoundingBox();
    const meshBox = offsetBox(
      geometry.boundingBox,
      position.x,
      position.y,
      position.z,
    );
    return meshBox;
  }
}

//#endregion

export {
  convertDwvPosition,
  getDwvPos,
  generateCrossSectionReport,
  generateCutsReport,
  getXAngle,
  getOppositePoint,
  _reportApps as ReportApps,
  _reportDicomInitialized as ReportDicomInitialized,
  loadReportDicomApps,
  getImagesObjFromDwvPosition,
};
